import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Chip,
  Select,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Paper,
  Grid,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import LocationMapPigeon from "../components/LocationMapPigeon";
import axios from "axios";
import { createTheme, ThemeProvider, styled } from "@mui/material/styles";
import theme from "../components/Theme";
import EmojiObjectsIcon from "@mui/icons-material/EmojiObjects";

const MapView = () => {
  const getApiBaseUrl = () => {
    // Always use production backend (onrender)
    return import.meta.env.VITE_ENVPROD || 'https://activitynavigator-backend.onrender.com';
  };
  const apiBaseUrl = getApiBaseUrl();
  const [places, setPlaces] = useState([]);
  const [filteredPlaces, setFilteredPlaces] = useState([]);
  const [activeFilters, setActiveFilters] = useState({
    type: "All",
    age_category: [],
    tags: [],
    kid_friendly: false,
    disability_friendly: false,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        let filter = {};

        if (activeFilters.type !== "All") filter.type = activeFilters.type;
        if (activeFilters.age_category.length > 0)
          filter.age_category = activeFilters.age_category;

        if (activeFilters.tags.length > 0) filter.tags = activeFilters.tags;

        if (activeFilters.kid_friendly)
          filter["kid_friendly"] = activeFilters.kid_friendly;

        if (activeFilters.disability_friendly)
          filter["disability_friendly"] = activeFilters.disability_friendly;

        const queryParams = new URLSearchParams(filter);

        const response = await fetch(`${apiBaseUrl}/api/places?${queryParams}`);
        const data = await response.json();
        setPlaces(data);
        setFilteredPlaces(data);
      } catch (error) {
        console.error("Error fetching places:", error);
      }
    };

    fetchData();
  }, [activeFilters]);

  const handleFilterChange = (filter, value) => {
    setActiveFilters((prevFilters) => {
      let updatedFilters = { ...prevFilters };

      if (Array.isArray(updatedFilters[filter])) {
        if (updatedFilters[filter].includes(value)) {
          updatedFilters[filter] = updatedFilters[filter].filter(
            (item) => item !== value
          );
        } else {
          updatedFilters[filter] = [...updatedFilters[filter], value];
        }
      } else {
        updatedFilters[filter] = value;
      }
      if (
        typeof updatedFilters[filter] === "boolean" &&
        !updatedFilters[filter]
      ) {
        delete updatedFilters[filter];
      }

      return updatedFilters;
    });
  };

  const clearAllFilters = () => {
    setActiveFilters({
      type: "All",
      age_category: [],
      tags: [],
      kid_friendly: false,
      disability_friendly: false,
    });
  };

  return (
    <ThemeProvider theme={theme}>
      <Grid container spacing={2} padding={3} maxHeight="100vh">
        <Grid
          item
          xs={12}
          container
          justifyContent="center"
          alignItems="center"
        >
          <Grid item xs={3}>
            <Button
              variant="contained"
              color="primary"
              component={Link}
              to="/places"
              sx={{ m: 2, backgroundColor: theme.palette.primary.light }}
            >
              All Places
            </Button>
            <Button
              variant="contained"
              color="secondary"
              component={Link}
              to="/random-place"
              sx={{ m: 2 }}
              startIcon={<EmojiObjectsIcon />}
            >
              Inspire me
            </Button>
          </Grid>
        </Grid>
        <Grid item xs={4} sx={{ maxHeight: "70vh" }}>
          <Paper sx={{ pt: 2 }}>
            <Grid item sx={{ mx: 2 }}>
              <Grid item container justifyContent="center" alignItems="center">
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={clearAllFilters}
                >
                  Clear Filters
                </Button>
              </Grid>
              <Typography variant="subtitle1">Type:</Typography>
              <Select
                value={activeFilters.type}
                onChange={(e) => handleFilterChange("type", e.target.value)}
                fullWidth
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Outdoor">Outdoor</MenuItem>
                <MenuItem value="Indoor">Indoor</MenuItem>
              </Select>

              <Typography variant="subtitle1">Age Category:</Typography>
              {["baby", "child", "teen", "adult", "elderly"].map((category) => (
                <Chip
                  key={category}
                  label={category}
                  clickable
                  color={
                    activeFilters.age_category.includes(category)
                      ? "primary"
                      : "default"
                  }
                  onClick={() => handleFilterChange("age_category", category)}
                  sx={{ m: 0.25 }}
                />
              ))}

              <Typography variant="subtitle1">Activities:</Typography>
              {[
                "calisthenics",
                "weightlifting",
                "aerobics",
                "yoga",
                "tennis",
                "football",
                "volleyball",
                "table tennis",
                "chess",
                "running and jogging",
                "skating and blading",
                "surfing",
                "basketball",
                "general mobility",
                "hiking and trailing",
                "racing and carting",
                "kid playground",
              ].map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  clickable
                  color={
                    activeFilters.tags.includes(tag) ? "primary" : "default"
                  }
                  onClick={() => handleFilterChange("tags", tag)}
                  sx={{ m: 0.25 }}
                />
              ))}
              <Typography variant="subtitle1">Inclusivity:</Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={activeFilters.kid_friendly}
                      onChange={() =>
                        handleFilterChange(
                          "kid_friendly",
                          !activeFilters.kid_friendly
                        )
                      }
                    />
                  }
                  label="Kid Friendly"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={activeFilters.disability_friendly}
                      onChange={() =>
                        handleFilterChange(
                          "disability_friendly",
                          !activeFilters.disability_friendly
                        )
                      }
                    />
                  }
                  label="Disability Friendly"
                />
              </FormGroup>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={8} sx={{ maxHeight: "70vh" }}>
          <LocationMapPigeon
            places={filteredPlaces}
            onMarkerClick={(place) => {
              // Marker clicked
            }}
          />
        </Grid>
      </Grid>
    </ThemeProvider>
  );
};

export default MapView;
